import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Skeleton,
  Alert,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  PlayArrow as ActivateIcon,
  Pause as PauseIcon,
  Delete as ArchiveIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { useMissionTemplates, activateTemplate, pauseTemplate, deleteTemplate } from '../hooks/useMissions';
import { useMissionBuilder } from '../MissionBuilderContext';
import type { MissionTemplate, TemplateStatus } from '../types';

const STATUS_COLORS: Record<TemplateStatus, 'default' | 'success' | 'warning' | 'error'> = {
  draft: 'default',
  active: 'success',
  paused: 'warning',
  archived: 'error',
};

const TYPE_LABELS: Record<string, string> = {
  stock_take: 'Stock Take',
  verify_product: 'Verify Product',
};

const TRIGGER_LABELS: Record<string, string> = {
  idle_time: 'Idle Time',
  interval: 'Interval',
  time_of_day: 'Scheduled',
  manual: 'Manual',
};

export function MissionList() {
  const { data, loading, error, refresh } = useMissionTemplates();
  const { setView, loadTemplate, resetBuilder, setSelectedTemplate } = useMissionBuilder();
  const notify = useNotify();

  const handleCreate = () => {
    resetBuilder();
    setView('builder');
  };

  const handleEdit = (template: MissionTemplate) => {
    loadTemplate(template);
  };

  const handleActivate = async (template: MissionTemplate) => {
    try {
      await activateTemplate(template.id);
      notify('Mission activated', { type: 'success' });
      refresh();
    } catch (e: any) {
      notify(e.message, { type: 'error' });
    }
  };

  const handlePause = async (template: MissionTemplate) => {
    try {
      await pauseTemplate(template.id);
      notify('Mission paused', { type: 'success' });
      refresh();
    } catch (e: any) {
      notify(e.message, { type: 'error' });
    }
  };

  const handleArchive = async (template: MissionTemplate) => {
    try {
      await deleteTemplate(template.id);
      notify('Mission archived', { type: 'success' });
      refresh();
    } catch (e: any) {
      notify(e.message, { type: 'error' });
    }
  };

  const handleViewInstances = (template: MissionTemplate) => {
    setSelectedTemplate(template);
    setView('instances');
  };

  if (loading && data.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={80} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={<Button onClick={() => refresh()}>Retry</Button>}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Mission Templates</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate} size="small">
          Create Mission
        </Button>
      </Box>

      {data.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No mission templates yet. Create your first one to get started.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleCreate}>
            Create Mission
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {data.map((template) => (
            <Paper
              key={template.id}
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                transition: 'all 0.15s',
                '&:hover': { boxShadow: 3 },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                    {template.name}
                  </Typography>
                  <Chip label={template.status} size="small" color={STATUS_COLORS[template.status]} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={TYPE_LABELS[template.missionType] || template.missionType} size="small" variant="outlined" />
                  <Chip label={TRIGGER_LABELS[template.triggerType] || template.triggerType} size="small" variant="outlined" />
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    {template.steps.length} step{template.steps.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                <Tooltip title="View instances">
                  <IconButton size="small" onClick={() => handleViewInstances(template)}>
                    <ViewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => handleEdit(template)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {template.status !== 'active' && template.status !== 'archived' && (
                  <Tooltip title="Activate">
                    <IconButton size="small" color="success" onClick={() => handleActivate(template)}>
                      <ActivateIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {template.status === 'active' && (
                  <Tooltip title="Pause">
                    <IconButton size="small" color="warning" onClick={() => handlePause(template)}>
                      <PauseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {template.status !== 'archived' && (
                  <Tooltip title="Archive">
                    <IconButton size="small" color="error" onClick={() => handleArchive(template)}>
                      <ArchiveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}
